import type { Frequency, RepeatKind } from '@/api/recurring'
import { DatePicker } from '@/components/DatePicker'
import { Checkbox } from '@/components/ui/checkbox'
import { Select } from '@/components/ui/select'
import { FREQUENCY_LABELS } from '@/lib/recurring'
import { Field } from './fields'

export interface RepeatsValue {
  on: boolean
  frequency: Frequency
  kind: RepeatKind
  /** null = follow the default (the payment date plus one period). */
  nextDueDate: string | null
}

// eslint-disable-next-line react-refresh/only-export-components -- the form and tests import these helpers alongside the component
export const REPEATS_OFF: RepeatsValue = {
  on: false,
  frequency: 'MONTHLY',
  kind: 'OTHER',
  nextDueDate: null,
}

const MONTHS: Record<Frequency, number> = {
  WEEKLY: 0,
  MONTHLY: 1,
  QUARTERLY: 3,
  HALF_YEARLY: 6,
  YEARLY: 12,
}

/** One period after `date` (YYYY-MM-DD), clamping month-ends the way the server's plusMonths does. */
// eslint-disable-next-line react-refresh/only-export-components -- see REPEATS_OFF
export function nextDueAfter(date: string, frequency: Frequency): string {
  const [y, m, d] = date.split('-').map(Number)
  if (frequency === 'WEEKLY') {
    return new Date(Date.UTC(y, m - 1, d + 7)).toISOString().slice(0, 10)
  }
  const total = m - 1 + MONTHS[frequency]
  const year = y + Math.floor(total / 12)
  const month = total % 12
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const day = Math.min(d, lastDay)
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// eslint-disable-next-line react-refresh/only-export-components -- see REPEATS_OFF
export function effectiveNextDue(v: RepeatsValue, date: string): string {
  return v.nextDueDate ?? nextDueAfter(date, v.frequency)
}

// eslint-disable-next-line react-refresh/only-export-components -- see REPEATS_OFF
export function repeatsError(v: RepeatsValue, date: string): string | null {
  if (!v.on) return null
  return effectiveNextDue(v, date) > date ? null : 'Next due must be after the payment date'
}

const KIND_OPTIONS: [RepeatKind, string][] = [
  ['BILL', 'Bill'],
  ['SUBSCRIPTION', 'Subscription'],
  ['OTHER', 'Other'],
]

export function RepeatsSection({
  value,
  onChange,
  date,
  isExpense,
}: {
  value: RepeatsValue
  onChange: (v: RepeatsValue) => void
  date: string
  isExpense: boolean
}) {
  return (
    <div className="rounded-lg border border-[var(--fl-border)] px-3 py-2">
      <label className="flex items-center gap-2 text-sm font-medium text-[var(--fl-text-1)]">
        <Checkbox
          checked={value.on}
          onChange={(e) => onChange({ ...value, on: e.target.checked })}
          aria-label="Repeats"
        />
        Repeats
      </label>
      {value.on && (
        <div className="mt-3 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Frequency">
              <Select
                aria-label="Frequency"
                value={value.frequency}
                onChange={(v) => onChange({ ...value, frequency: v as Frequency })}
              >
                {Object.entries(FREQUENCY_LABELS).map(([f, label]) => (
                  <option key={f} value={f}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            {isExpense && (
              <Field label="Kind">
                <Select
                  aria-label="Kind"
                  value={value.kind}
                  onChange={(v) => onChange({ ...value, kind: v as RepeatKind })}
                >
                  {KIND_OPTIONS.map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
          <Field label="Next due">
            <DatePicker
              value={effectiveNextDue(value, date)}
              onChange={(d) => onChange({ ...value, nextDueDate: d })}
            />
          </Field>
          <p className="text-xs text-[var(--fl-text-2)]">
            Adds a reminder on Recurring. This payment counts as the first one.
          </p>
        </div>
      )}
    </div>
  )
}
